import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const gettingStartedLinks = [
  { title: 'API Reference explained', href: '#api-explained' },
  { title: 'Getting your API key', href: '#getting-api-key' },
  { title: 'Authentication and security', href: '#authentication' },
  { title: 'Making your first API request', href: '#first-request' },
];

const endpointLinks = [
  { title: 'Chat API', href: '#chat-api' },
  { title: 'Employees API', href: '#employees-api' },
  { title: 'Conversations API', href: '#conversations-api' },
  { title: 'Tasks API', href: '#tasks-api' },
  { title: 'Brain AI API', href: '#brain-api' },
  { title: 'Usage API', href: '#usage-api' },
  { title: 'Webhooks API', href: '#webhooks-api' },
];

const sdkLinks = [
  { title: 'JavaScript/TypeScript SDK', href: '#javascript-sdk' },
  { title: 'Python SDK', href: '#python-sdk' },
  { title: 'Ruby SDK', href: '#ruby-sdk' },
  { title: 'PHP SDK', href: '#php-sdk' },
  { title: 'Go SDK', href: '#go-sdk' },
];

const advancedLinks = [
  { title: 'Rate limits and quotas', href: '#rate-limits' },
  { title: 'Error handling', href: '#error-handling' },
  { title: 'Pagination', href: '#pagination' },
  { title: 'Webhooks and callbacks', href: '#webhooks-callbacks' },
  { title: 'Best practices', href: '#best-practices' },
];

const faqLinks = [
  { title: 'API FAQ', href: '#api-faq' },
  { title: 'Troubleshooting API issues', href: '#troubleshooting' },
];

interface LinkItemProps {
  title: string;
  href: string;
  isDark: boolean;
}

function LinkItem({ title, href, isDark }: LinkItemProps) {
  return (
    <Link
      to={href}
      className={`flex items-center justify-between py-4 px-2 transition-all group border-b ${
        isDark
          ? 'border-white/5 hover:bg-white/5'
          : 'border-slate-100 hover:bg-slate-50'
      }`}
    >
      <span className={`font-medium transition-colors ${
        isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
      }`}>
        {title}
      </span>
      <ChevronRight className={`w-5 h-5 transition-colors ${
        isDark ? 'text-gray-600 group-hover:text-purple-400' : 'text-slate-300 group-hover:text-purple-600'
      }`} />
    </Link>
  );
}

interface SectionCardProps {
  title: string;
  links: Array<{ title: string; href: string }>;
  isDark: boolean;
  delay?: number;
}

function SectionCard({ title, links, isDark, delay = 0 }: SectionCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`rounded-2xl border overflow-hidden ${
        isDark
          ? 'bg-dark-800/50 border-white/5'
          : 'bg-white border-slate-200 shadow-sm'
      }`}
    >
      <div className={`px-6 py-5 border-b ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
        <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
      </div>
      <div className="px-4">
        {links.map((link) => (
          <LinkItem
            key={link.title + link.href}
            title={link.title}
            href={link.href}
            isDark={isDark}
          />
        ))}
      </div>
    </motion.div>
  );
}

export default function APIReferenceDocs() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title=""
      subtitle=""
      showBackButton={false}
    >
      {/* Breadcrumbs */}
      <nav className="mb-8">
        <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <li>
            <Link
              to="/docs"
              className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
            >
              All Collections
            </Link>
          </li>
          <li>
            <ChevronRight className="w-4 h-4" />
          </li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            API Reference
          </li>
        </ol>
      </nav>

      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <h1 className={`text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          API Reference
        </h1>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Build custom integrations with our REST API.
        </p>
      </motion.div>

      {/* Getting Started */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`rounded-2xl border overflow-hidden mb-6 ${
          isDark
            ? 'bg-dark-800/50 border-white/5'
            : 'bg-white border-slate-200 shadow-sm'
        }`}
      >
        <div className="px-4">
          {gettingStartedLinks.map((link) => (
            <LinkItem
              key={link.title}
              title={link.title}
              href={link.href}
              isDark={isDark}
            />
          ))}
        </div>
      </motion.div>

      {/* API Endpoints */}
      <SectionCard
        title="API Endpoints"
        links={endpointLinks}
        isDark={isDark}
        delay={0.2}
      />

      <div className="h-6" />

      {/* SDKs & Libraries */}
      <SectionCard
        title="SDKs & Libraries"
        links={sdkLinks}
        isDark={isDark}
        delay={0.3}
      />

      <div className="h-6" />

      {/* Advanced Topics */}
      <SectionCard
        title="Advanced Topics"
        links={advancedLinks}
        isDark={isDark}
        delay={0.4}
      />

      <div className="h-6" />

      {/* FAQ & Troubleshooting */}
      <SectionCard
        title="Help & Support"
        links={faqLinks}
        isDark={isDark}
        delay={0.5}
      />

      <div className="h-16" />

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="text-center"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">{"</>"}</span>
        </div>
        <a
          href="mailto:help@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          help@sweetpilot.com
        </a>
      </motion.div>
    </StaticPageLayout>
  );
}
